import React from 'react'
import { useSelector } from 'react-redux'

import Button from '@mui/material/Button';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';

import _ from 'lodash';
import dayjs from 'dayjs';
import { describeDate, describeTime } from '../utils';

const EnrolmentsPdf = ({subscriptions, enrolments, waitlists, interests, displayRange, timetableRange, interestsName, isSm}) => {
	const devMode = window.$umas4m_config.dev_mode;
	
	const generalSettings = useSelector(state => state.settings.general.data);
	const member = useSelector(state => state.member.data);
	const logo = useSelector(state => state.requests.logo);
	const pdfFonts = useSelector(state => state.pdfFonts);
	
	const tableLayouts = {
		u3aLayout: {
			hLineWidth: function (i, node) {
				if(i === 0 ) {
					return 0;
				}
				return (i === 1 || i === node.table.body.length) ? 2 : 1;
			},
			vLineWidth: function (i) {
				return 0;
			},
			hLineColor: function (i) {
				return '#2a55a0';
			},
			paddingLeft: function (i) {
				return 4;
			},
			paddingRight: function (i) {
				return 4;
			},
			paddingTop: function (i) {
				return 8;
			},
			paddingBottom: function (i) {
				return 8;
			}
		},
	}
	
	const docDefinition = {
		pageSize: 'A4',
		pageMargins: [28, 42, 28, 72],
		defaultStyle: {
			fontSize: 10,
		},
		styles: {
			header: {
				fontSize: 28,
				bold: true,
				margin: [0, 0, 0, 24]
			},
			h2: {
				fontSize: 22,
				bold: true,
				margin: [0, 24, 0, 16]
			},
			doubleParagraph: {
				margin: [0, 0, 0, 16]
			},
			paragraph: {
				margin: [0, 0, 0, 8]
			},
			th: {
				bold: true,
				fillColor: '#ffe793',
			},
			deleted: {
				decoration: 'lineThrough',
				color: '#aaaaaa',
			},
			unpaid: {
				color: '#ff0000'
			},
			numCol: {
				alignment: 'right'
			},
			note: {
				fontSize: 11,
				italics: true
			},
			noneFound: {
				alignment: 'center',
				italics: true,
			}
		}
	}
	
	const formatField = (type, value, deleted = false, unpaid = false) => {
		const field = deleted ? { style: 'deleted' } : {}
		switch(type){
			case 'start_date':
			case 'finish_date':
				field.text = describeDate(value);
				field.alignment = 'right';
				break;
			case 'start_time':
				field.text = describeTime(value);
				field.alignment = 'right';
				break;
			case 'amount_charged':
			case 'amount_paid':
				field.text = '$' + value;
				field.alignment = 'right';
				break;
			case 'invoice_no':
				field.alignment = 'right';
				if(unpaid){
					field.text = member.member_no + '-' + value + ' (unpaid)';
					field.style = 'unpaid';
				} else {
					field.text = member.member_no + '-' + value;
				}
				break;
			case 'points':
				field.text = value;
				field.alignment = 'right';
				break;
			case 'course_name':
				field.text = deleted ? value + ' (deleted)' : value;
				break;
			default: // course_code, interest_code, short_comment, venue_code
				field.text = value;
		}
		return field;
	}

	const downloadPdf = () => {

		const subsItems = subscriptions.length === 0 ? 
		[[{text: 'You have no membership subscriptions for '+displayRange, colSpan: 6, style: 'noneFound'}, '','','','','']] :
		_.map(subscriptions, i => {
			return [
				formatField('course_code', i.course_code),
				formatField('course_name', i.course_name),
				formatField('start_date', i.start_date),
				formatField('finish_date', i.finish_date),
				formatField('amount_charged', i.amount_charged),
				formatField('invoice_no', i.invoice_no, false, _.toNumber(i.amount_charged) > _.toNumber(i.amount_paid))
			];
		});
		
		subsItems.unshift([
			{text: 'Code', style: 'th' },
			{text: 'Name', style: 'th' },
			{text: 'Start Date', style: 'th', alignment: 'right'  },
			{text: 'End Date', style: 'th', alignment: 'right'  },
			{text: 'Fee', style: 'th', alignment: 'right' },
			{text: 'Invoice', style: 'th', alignment: 'right' }
		]);
		
		const enrolItems = enrolments.length === 0 ? 
		[[{text: 'You have no enrolments for '+displayRange, colSpan: 7, style: 'noneFound'}, '','','','','','']] : 
		_.map(enrolments, i => {
			return [
				formatField('course_code', i.course_code, i.status === 'Deleted'),
				formatField('course_name', i.course_name, i.status === 'Deleted'),
				formatField('start_date', i.start_date, i.status === 'Deleted'),
				formatField('start_time', i.start_time, i.status === 'Deleted'),
				formatField('finish_date', i.finish_date, i.status === 'Deleted'),
				formatField('venue_code', i.venue_code, i.status === 'Deleted'),
				formatField('invoice_no', i.invoice_no, false, _.toNumber(i.amount_charged) > _.toNumber(i.amount_paid))
			];
		});
		
		enrolItems.unshift([
			{text: 'Code', style: 'th' },
			{text: 'Name', style: 'th' },
			{text: 'Start Date', style: 'th', alignment: 'right'  },
			{text: 'Start Time', style: 'th', alignment: 'right' },
			{text: 'End Date', style: 'th', alignment: 'right'  },
			{text: 'Venue', style: 'th' },
			{text: 'Invoice', style: 'th', alignment: 'right' }
		]);
		
		const waitItems = waitlists.length === 0 ? 
		[[{text: 'You have no current waitlist enrolments', colSpan: 6, style: 'noneFound'}, '','','','','']] : 
		_.map(waitlists, i => {
			return [
				formatField('course_code', i.course_code),
				formatField('course_name', i.course_name),
				formatField('start_date', i.start_date),
				formatField('start_time', i.start_time),
				formatField('finish_date', i.finish_date),
				formatField('venue_code', i.venue_code)
			];
		});
		
		waitItems.unshift([
			{text: 'Code', style: 'th' },
			{text: 'Name', style: 'th' },
			{text: 'Start Date', style: 'th', alignment: 'right'  },
			{text: 'Start Time', style: 'th', alignment: 'right' },
			{text: 'End Date', style: 'th', alignment: 'right'  },
			{text: 'Venue', style: 'th' },
		]);
	

		
		const u3aLogo = logo.image ? {
			image: logo.image,
			fit: [210, 50]
		} : {
			width: '*',
			text: '[ '+generalSettings.display_name+' logo ]',
			italics: true,
		};

		const content = [
			{
				columns: [
					{
						width: '60%',
						text: 'ENROLMENTS', 
						style: 'header'
					}, 
					u3aLogo
				],
				columnnGap: 10
			},
			{
				columns: [
					{
						width: '60%',
						stack: [
							{text: generalSettings.display_name, bold: true, style: 'doubleParagraph', fontSize: 12 },
							{text: member.full_name, bold: true, fontSize: 12},
							{text: 'Member no: '+ member.member_no, style: 'doubleParagraph', fontSize: 12},
						]
					}, 
					{
						width: '*',
						stack: [
							{text: 'Downloaded: ' + dayjs().format("D MMM YYYY, h:mm a"), style: 'note' },
						]
					}
				],
				columnnGap: 10
			},
			{text: 'Membership Subscriptions', style: 'h2'},
			{text: displayRange, style: 'doubleParagraph', fontSize: 12},
			{
				layout: 'u3aLayout',
				table: {
					headerRows: 1,
					widths: ['*', '*', '*', '*', '*', '*'],
					body: subsItems,
					dontBreakRows: true
				}
			},
			{text: 'Enrolments', style: 'h2'},
			{text: displayRange, style: 'doubleParagraph', fontSize: 12},
			{
				layout: 'u3aLayout',
				table: {
					headerRows: 1,
					widths: ['*', '*', '*', '*', '*', '*', '*'],
					body: enrolItems,
					dontBreakRows: true
				}
			},
			{text: 'Waitlist Enrolments', style: 'h2'},
			{text: timetableRange, style: 'doubleParagraph', fontSize: 12},
			{
				layout: 'u3aLayout',
				table: {
					headerRows: 1,
					widths: ['*', '*', '*', '*', '*', '*'],
					body: waitItems,
					dontBreakRows: true
				}
			},
			
		];
		
		if(interests !== false){
			const specItems = interests.length === 0 ?
			[[{text: 'You have no ' + interestsName + ' items', colSpan: 3, style: 'noneFound'}, '', '']] : 
			_.map(interests, i => {
				return [
					formatField('interest_code', i.interest_code),
					formatField('short_comment', i.short_comment),
					formatField('points', i.points)
				];
			});
			
			specItems.unshift([
				{text: 'Code', style: 'th' },
				{text: 'Note', style: 'th' },
				{text: 'Points', style: 'th', alignment: 'right'}
			]);
			
			content.push({text: interestsName, style: 'h2'},
			{
				layout: 'u3aLayout',
				table: {
					headerRows: 1,
					widths: ['*', '*', '*'],
					body: specItems,
					dontBreakRows: true
				}
			});
		}
		
		const enrolmentsDefinition = {...docDefinition, content: content };
		
		/*
		pdfMake increases bundle size ridiculously, 
		so split it out and only load if we need it
		*/
		import('pdfmake/build/pdfmake').then(pdfmake => {
			
			if(devMode){
				pdfmake.createPdf(enrolmentsDefinition, tableLayouts, pdfFonts).open();
			} else {
				pdfmake.createPdf(enrolmentsDefinition, tableLayouts, pdfFonts).download('Enrolments_member_'+member.member_no+'.pdf');
			}
		})

	}

		return <Button color="primary" 
			startIcon={<DescriptionOutlinedIcon />}
			onClick={(e) => downloadPdf()}
		>{isSm ? 'Download PDF' : 'PDF'}</Button>
	
}
export default EnrolmentsPdf